import axios from 'axios'

import { APP_TITLE, APP_VERSION } from '@/init/settings'
import { AuthUser } from '@/models/interfaces'
import { backLink } from '@/models/backlink'
import { auth } from '@/services/auth'
import { request } from '@/services/request'
import { waitWithTimeout } from '@/helpers/timer'
import { Sentry } from '@/services/sentry'

import { isBot } from '@/helpers/tracking'
import { storage } from '@/helpers/storage'
import { experimentsData, clientId } from '@/services/ab'
import { platform } from '@/services/platform'
import { router } from '@/helpers/router'
import { AuthType } from '@/models/interfaces.auth'
import {
  FIRST_PAGE_KEY,
  MARKETING_URL_KEY,
  HISTORY_KEY,
  REF_KEY,
  REF_DOMAIN_KEY,
} from './keys'

function referringDomain(referrer: string): string {
  const split = referrer.split('/')
  if (split.length >= 3) {
    return split[2]
  }
  return ''
}

export async function pageView(
  routeParams?: Record<string, any>,
  isRetry: boolean = false,
): Promise<void> {
  // We have page load check on pingdom.
  if (isBot()) {
    return
  }

  try {
    if (!isRetry) {
      // Save first visited page to local storage,
      // we will set it as people property on signup.
      _saveFirstPage()
      // Save marketing URL.
      detectAndSaveMarketingUrl()
    }

    // Check if the user is logged or in the login, onboarding or pilot pages.
    const user = auth.getUser()
    if (
      !user &&
      ![
        '/app/login',
        '/app/onboarding',
        '/pilot/extension',
        '/pilot/digest',
        '/pilot/read-it-later',
      ].includes(window.location.pathname)
    ) {
      // User has not signed up, collect page view, but don't track it yet.
      // We allow tracking in the login and onboarding pages.
      _collectEvent()
      return
    }

    // If mixpanel is not loaded yet, try later.
    //
    // Note: This should only be useful for pageView which we call
    // on page load in `router.afterEach`.
    //
    // Note: we set the `__MIXPANEL_LOADED` in the `loaded` callback
    // that we pass to `mixpanel.init`, see ../main.ts.
    if ((window as any).__MIXPANEL_LOADED !== true) {
      setTimeout(() => {
        pageView(routeParams, true)
      }, 300)
      return
    }

    if (user) {
      _setMixpanelUserPropertiesLoggedInUser()
    } else {
      _setPublicUserProperties()
    }

    mixpanel.track('Page View', { 'Route Parameters': routeParams })
  } catch (error) {
    Sentry.captureException(error)
  }
}

export async function register(
  user: AuthUser,
  signupType: AuthType,
  done: () => void,
): Promise<void> {
  if (!hasMixpanel()) {
    return
  }

  try {
    // This is the other half of the logic in _saveFirstPage().
    // Adds first visited page as people profile property.
    // We set this only once, on Signup.
    const signupProperties = await getMixpanelSignupProperties(
      user.temporary_account_state,
    )
    mixpanel.people.set(signupProperties)
    mixpanel.people.set_once({ 'Signup Type': signupType })

    _setMixpanelUserProperties(user, false)

    // Up until this point, any events from this device were logged
    // with a randomly generated `distinct_id` set by Mixpanel.
    // Here we set `user.id` as an alias for that random `distinct_id`.
    // If we called `mixpanel.identify()`, it would create a new user
    // with `distinct_id` set to `user.id`. Related documentation:
    // https://github.com/mixpanel/docs/blob/main/legacy/aliases.md
    mixpanel.alias(user.id)

    // Track a batch of page views collected before the singup.
    _trackCollectedEvents(() => {
      mixpanel.track(
        'Signup',
        {
          'Signup Type': signupType,
        },
        done,
      )
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

/**
 * Get the properties to set in the user's Mixpanel profile after Signup.
 */
export async function getMixpanelSignupProperties(
  temporaryAccountState?: string | null,
): Promise<Record<string, any>> {
  const deviceInfo = await platform.deviceInfo()
  const firstPageData = getFirstPageData()

  return {
    Signup: new Date(),
    'Signup Device': deviceInfo.model,
    'Signup Platform': deviceInfo.platform,
    'First Page': firstPageData.firstPage,
    'Marketing URL': firstPageData.marketingUrl,
    'Initial Referrer SF': firstPageData.initialReferrer,
    'Temporary Account State': temporaryAccountState,
  }
}

export function login(user: AuthUser, done: () => void): void {
  if (!hasMixpanel()) {
    return
  }

  // We have login check on pingdom.
  if (isBot()) {
    return
  }
  try {
    mixpanel.people.set({
      'Last Login': new Date().toISOString(),
    })
    mixpanel.people.increment({
      Logins: 1,
    })
    // We need to call 'identify' after setting profile properties,
    // it is called inside the _setMixpanelUserProperties.
    _setMixpanelUserProperties(user)
    mixpanel.track('Login', undefined, done)

    // We only need to keep the history for users who didn't signup yet.
    // If the user logs in, it is safe to clear the history.
    // This may not work property if two different people are using the
    // same device / browser, but it should be rare enough to ignore and
    // better than having ever-growing history item in the local storage.
    _clearCollectedEvents()
  } catch (error) {
    Sentry.captureException(error)
  }
}

export function logout(): void {
  if (!hasMixpanel()) {
    return
  }

  try {
    mixpanel.people.set({
      'Last Logout': new Date().toISOString(),
    })
    mixpanel.people.increment({
      Logouts: 1,
    })
    _setMixpanelUserPropertiesLoggedInUser()
    mixpanel.track('Logout')
    mixpanel.reset()
  } catch (error) {
    Sentry.captureException(error)
  }
}

/**
 * Set properties on a user record only if they don't exist yet.
 */
export function setOnce(data: Record<string, unknown>): void {
  if (!hasMixpanel()) {
    return
  }

  try {
    mixpanel.people.set_once(data)
  } catch (error) {
    Sentry.captureException(error)
  }
}

/**
 * Track the event and optionally set people properties.
 *
 * Minimal example:
 *
 *   _track('My Event')
 *
 * Full example:
 *
 *   _track(
 *     // Event to track.
 *     'Select Book Category',
 *     // Event data.
 *     {
 *       category: tag,
 *     },
 *     // Data for mixpanel.people.set({..}).
 *     {
 *       'Last Book Category Select': new Date().toISOString(),
 *     },
 *     // Data for mixpanel.people.increment({..}).
 *     {
 *       'Book Category Selected': 1,
 *     },
 *     // Callback to execute after tracking.
 *     () => {
 *       console.log('done')
 *     }
 *   )
 */
export function _track(
  event: string,
  eventData?: Record<string, unknown>,
  peopleSetData?: Record<string, unknown>,
  peopleIncrementData?: Record<string, unknown>,
  callback?: (err?: any) => void,
): void {
  if (!hasMixpanel()) {
    return
  }

  try {
    if (peopleSetData) {
      mixpanel.people.set(peopleSetData)
    }
    if (peopleIncrementData) {
      mixpanel.people.increment(peopleIncrementData)
    }
    if (peopleSetData || peopleIncrementData) {
      // This should be after `mixpanel.people` calls (as it calls `identify`)
      // and before `track` (as it call `register`).
      _setMixpanelUserPropertiesLoggedInUser()
    }
    if (eventData || callback) {
      mixpanel.track(event, eventData || {}, callback)
    } else {
      mixpanel.track(event)
    }
  } catch (error) {
    Sentry.captureException(error)
    if (callback) {
      callback(error)
    }
  }
}

/**
 * Async version of the '_track' call.
 *
 * Note: this is better in terms of logic - most of the time we don't await the
 * tracking is it is not super essentaial, also this is not good for tests
 * - the calls left without wating for them and might be executed at
 * the point where the call will fail.
 * For example, in the trackBookReadTime test, where the tracking call is
 * invoked from the beforeRouteLeave it resulted in `book` parameter being
 * null (although we defenitely set it when we invoke the method).
 * Adding `async` helped solving it, but we still have many such cases with
 * other tracking calls.
 */
export async function _trackAsync(
  event: string,
  eventData?: Record<string, unknown>,
  peopleSetData?: Record<string, unknown>,
  peopleIncrementData?: Record<string, unknown>,
): Promise<void> {
  return await waitWithTimeout(
    (done) => {
      _track(event, eventData, peopleSetData, peopleIncrementData, done)
    },
    1,
    100,
  )
}

/**
 * Set basic profile properties for users on public pages.
 */
function _setPublicUserProperties(): void {
  const firstPageData = getFirstPageData()

  setOnce({
    'First Page Public User': firstPageData.firstPage,
    'Marketing URL Public User': firstPageData.marketingUrl,
    'Initial Referrer SF Public User': firstPageData.initialReferrer,
    ...experimentsData(),
    client_id: clientId() || undefined,
  })

  // We must call identify to associate the profile update with the user.
  mixpanel.identify()
}

/**
 * Set Mixpanel super properties and people profile properties
 * for logged in user.
 */
export function _setMixpanelUserPropertiesLoggedInUser(): void {
  const user = auth.getUser()
  if (user) {
    _setMixpanelUserProperties(user)
  }
}

/**
 * Set Mixpanel super properties and people profile properties.
 *
 * Also calls 'identify' to actually send people profile changes to mixpanel.
 * This means, we should call this method after doing `mixpanel.people.xxx`
 * and before `mixpanel.track` (as this method also calls `register` to
 * set superproperties).
 */
export function _setMixpanelUserProperties(
  user: AuthUser,
  callIdentify: boolean = true,
): void {
  if (!hasMixpanel()) {
    return
  }

  const abData = experimentsData()
  // People profile properties.
  const properties: any = {
    $name: user.email,
    $email: user.email,
    Email: user.email,
    Username: user.username,
    Plan: user.plan_id,
    Guid: user.id,
    Language: user.language,
    IsLimited: user.is_limited,
    ...abData,
  }
  mixpanel.people.set(properties)

  // Super properties, added to all "track" calls.
  // Note: those are the same as in people profile,
  // the difference is that super properties are added to events,
  // so we will track changes over time, while in the profile
  // we only have the latest static value.
  const superproperties: any = {
    $name: user.email,
    $email: user.email,
    Email: user.email,
    Username: user.username,
    Plan: user.plan_id,
    Guid: user.id,
    IsLimited: user.is_limited,
  }
  mixpanel.register(superproperties)

  // The Mixpanel library does not automatically create people profiles for any
  // user that performs an event. In order to send profile updates, you must call
  // mixpanel.identify in addition to mixpanel.people.set, which empowers you to
  // create profiles for only the users of your choice.
  if (callIdentify) {
    mixpanel.identify(user.id)
  }
}

/**
 * We want to save the first visited page to Mixpanel on signup.
 * We do it in a following way:
 * - On every page visit:
 *    - Check if we have first_page cookie set
 *    - If not: save window.location as first_page cookie
 * - On sign up
 *    - Set first_page cookie value as "First page seen"
 *    people profile property for logged in user.
 *
 * NOTE: this code also installed on Wordpress blog:
 *
 *   <!-- track first page -->
 *   <script type="text/javascript">
 *       const data = localStorage.getItem('sf_first_page');
 *       if (!data) {
 *        localStorage.setItem('sf_first_page', window.location.href);
 *        localStorage.setItem('sf_referrer', document.referrer || '$direct');
 *        localStorage.setItem(
 *          'sf_referrng_domain',
 *          referringDomain(document.referrer) || '$direct',
 *        );
 *      }
 *   </script>
 */
export function _saveFirstPage(): void {
  const data = storage.getItem(FIRST_PAGE_KEY)
  if (!data) {
    // If there's no First Page data in local storage,
    // it shouldn't have previous referrer either.
    // We're checking this for debugging in #17355.
    const previousReferrer = storage.getItem(REF_KEY)

    storage.setItem(FIRST_PAGE_KEY, window.location.href)
    storage.setItem(REF_KEY, document.referrer || '$direct')
    storage.setItem(
      REF_DOMAIN_KEY,
      referringDomain(document.referrer) || '$direct',
    )

    // Some additional data for debugging in Sentry.
    const sentryAdditionalData = {
      currentURL: window.location.href,
      referrer: document.referrer,
      previousReferrer: previousReferrer,
      history: storage.getItem(HISTORY_KEY),
      marketingURL: storage.getItem(MARKETING_URL_KEY),
      sfNeedsUpdate: storage.getItem('sf_needs_update'),
      user: storage.getItem('user'),
      previouslyLoggedIn: storage.getItem('previously_logged_in'),
    }

    try {
      // This is the unexpected case we're trying to debug: initial referrer
      // being set as Shortform. If this is the user's first time visiting
      // shortform, the referrer should be coming from somewhere else.
      if (referringDomain(document.referrer).includes('shortform.com')) {
        window.Sentry?.captureMessage(
          'Setting shortform.com as the initial referrer',
          {
            extra: sentryAdditionalData,
          },
        )
      } else {
        // This is the expected case. We set it as a breadcrumb in case we get an
        // unexpected case later, we can check if it had set the proper data before,
        // but for some reason was overwritten.
        window.Sentry?.addBreadcrumb({
          message: `Setting first page data: ${window.location.href}`,
          data: sentryAdditionalData,
        })
      }
    } catch (error) {}
  }
}

/**
 * Save marketing URL on page load.
 *
 * On the website page visit (www.shortform.com),
 * check if we have utm_source=xxx&utm_medium=partner parameters
 *
 * If the user is logged in: send a backend request with current URL.
 * Backend saves it (as user.marketing_url) and decides if the
 * discount is applicable.
 * If the user is not logged in: save it to the browser local
 * storage as marketing_url.
 *
 * Note: this is not directly related to mixpanel, but it is convenient
 * to handle marketing URL here, similar to the first url.
 */
export async function detectAndSaveMarketingUrl(): Promise<void> {
  const marketingURL = detectMarketingURL()
  if (!marketingURL) {
    return
  }

  const user = auth.getUser()
  if (user) {
    // For logged in user - send the URL to the backend.
    await request.post('billing/marketing_url', { marketing_url: marketingURL })
  } else {
    // For not logged in user - keep the first marketing_url
    // in the local storage (do not overwrite it).
    const data = storage.getItem(MARKETING_URL_KEY)
    if (!data) {
      storage.setItem(MARKETING_URL_KEY, marketingURL)
    }
  }
}

/**
 * Return the current or referrer URL if either of them is a marketing URL.
 *
 * We use the referrer URL as a fallback (in case the script failed to load
 * in the previous page).
 */
function detectMarketingURL(): string | undefined {
  const currentURL = window.location.href
  const referrer = document.referrer

  if (currentURL.includes('utm_source')) {
    return currentURL
  }

  if (
    referringDomain(referrer).includes('shortform.com') &&
    referrer.includes('utm_source')
  ) {
    return referrer
  }
}

/**
 * Collect the page view to track it later, after signup.
 * This reduces mixpanel usage: we don't track users who
 * don't signup.
 *
 * NOTE: this code also installed on Wordpress blog:
 *
 *   <script type="text/javascript">
 *   function trackPageView() {
 *     const ua = window.navigator.userAgent.toLowerCase();
 *     if (ua.includes('pingdom')) {
 *         return;
 *     }
 *     const email = mixpanel.get_property('Email');
 *     if (email === undefined) {
 *       collectPageView();
 *       return;
 *     }
 *     mixpanel.track('Page View');
 *   };
 *   function collectPageView() {
 *     const storageData = localStorage.getItem('sf_history');
 *     const historyData = storageData ? JSON.parse(storageData) : [];
 *     const currentPage = window.location.href;
 *     const lastPage = historyData[historyData.length - 1];
 *     if (!lastPage || currentPage !== lastPage[1]) {
 *       historyData.push([Math.floor(new Date().getTime() / 1000), currentPage]);
 *     }
 *     localStorage.setItem('sf_history', JSON.stringify(historyData));
 *   };
 *   </script>
 *   <!-- start Mixpanel --><script type="text/javascript">
 *   // ...
 *   mixpanel.init("628b13b57f789d481577b13542b60034", {loaded: trackPageView});
 *   </script><!-- end Mixpanel -->
 */
export function _collectEvent(eventName?: string): void {
  try {
    const storageData = storage.getItem(HISTORY_KEY)
    const historyData = storageData ? JSON.parse(storageData) : []

    const currentPage = window.location.href
    const lastPage = historyData[historyData.length - 1]
    if (
      !lastPage ||
      !(currentPage === lastPage[1] && eventName === lastPage[2])
    ) {
      const data = [Math.floor(new Date().getTime() / 1000), currentPage]
      if (eventName) {
        data.push(eventName)
      }
      historyData.push(data)
    }
    storage.setItem(HISTORY_KEY, JSON.stringify(historyData))
  } catch (error) {
    Sentry.captureException(error)
    // Most likely the error is occured while parsing the history data,
    // so we clear it up and continue collecting from scratch.
    _clearCollectedEvents()
  }
}

/**
 * Track collected page views to mixpanel.
 *
 * This method is called on user signup.
 * It uses Mixpanel HTTP API to send the POST request with events,
 * to /track/ API method, the javascript SDK doesn't support the
 * batch call variant (alwyas sends GET request).
 *
 * See https://developer.mixpanel.com/docs/http#section-batch-requests
 *
 * See also mixpanel javascript SDK implementation, the `MixpanelLib.prototype._send_request`:
 * https://github.com/mixpanel/mixpanel-js/blob/f21586cf441b5461919bdba1d6257a2e9c084155/src/mixpanel-core.js#L882-L958
 *
 * and `MixpanelLib.prototype.track`:
 * https://github.com/mixpanel/mixpanel-js/blob/f21586cf441b5461919bdba1d6257a2e9c084155/src/mixpanel-core.js#L1050-L1134
 */
export function _trackCollectedEvents(done: () => void): void {
  if (!hasMixpanel()) {
    return
  }

  // The 'mixpanel' variable might be present, but if the adblocker or incognito
  // mode blocks mixpanel requests, it will not be intitialized and will not have
  // `get_config` method (as well as some other methods, like `get_property`, etc,
  // at the same time it will have some methods like `register`).
  if (typeof mixpanel.get_config === 'undefined') {
    return
  }

  try {
    // Get the collected data.
    const storageData = storage.getItem(HISTORY_KEY)
    const historyData = storageData ? JSON.parse(storageData) : []
    const initialReferrerData = getInitialReferrer()

    // Overwrite referrer and referring domain super properties.
    mixpanel.register(initialReferrerData)

    let previousEventURL = ''
    // Convert to array of Mixpanel events.
    const events = historyData.map((item: [number, string, string?]) => {
      // Event data structure: [
      //    number - timestamp
      //    string - page URL
      //    string - optional event name, 'Page View' by default
      // ]

      // The call below returns standard properties, for example:
      // {
      //  "$os": "Linux", "$browser": "Firefox",
      //  "$current_url": "http://localhost:8080/app",
      //  "$browser_version": 68,
      //  "$screen_height": 1026, "$screen_width": 1824,
      //  mp_lib: "web",
      //  "$lib_version": "2.29.0",
      //  time: 1565080782.652
      // }
      //
      // In general, this might not be 100% accurate (like the user might
      // change the screen resolution, or mixpanel library might be updated
      // in the middle of the events collection), but should be good enough
      // in most cases.
      //
      // Note: this is also a bit risky as we use the internal detail, this
      // method might be changed or removed or the method name uglified.
      // In that case we should start getting Sentry errors and will be able
      // to fix that.
      // In some cases, mixpanel._ might not be available (while `mixpanel` itself
      // is usable), it this case - just start with the empty dictionary.
      const properties = mixpanel._ ? mixpanel._.info.properties() : {}

      // Add token and distinct_id.
      properties['token'] = mixpanel.get_config('token')
      properties['distinct_id'] = mixpanel.get_distinct_id()

      // Replace `$current_url` and `time` with the data we have in local storage.
      properties['time'] = item[0]
      properties['$current_url'] = item[1]

      // Add super properties.
      properties['Email'] = mixpanel.get_property('Email')
      properties['$name'] = mixpanel.get_property('$name')
      properties['Guid'] = mixpanel.get_property('Guid')

      // Set the referrer for the event.
      if (previousEventURL) {
        // If we have previousEventURL, use it as a referrer.
        properties['$referrer'] = previousEventURL
        properties['$referring_domain'] =
          referringDomain(previousEventURL) || '$direct'
      } else {
        // Use initial referrer (no previousEventURL, so this is the first event).
        properties['$referrer'] =
          initialReferrerData.$initial_referrer || '$direct'
        properties['$referring_domain'] =
          initialReferrerData.$initial_referring_domain || '$direct'
      }

      previousEventURL = item[1]

      const eventName: string = item[2] || 'Page View'

      return {
        event: eventName,
        properties: properties,
      }
    })
    _trackBatch(events, done)
    _clearCollectedEvents()
  } catch (error) {
    Sentry.captureException(error)
    // Execute done, so we aren't stuck waiting for callback if some error occured.
    done()
  }
}

export function _clearCollectedEvents(): void {
  storage.removeItem(HISTORY_KEY)
}

/**
 * Send the batch request to Mixpanel /track API.
 *
 * The data is strigified and base64 encoded, see
 * https://developer.mixpanel.com/docs/http#section-batch-requests:
 *
 * > To send a batch of messages to an endpoint,
 * > you should use a POST instead of a GET request.
 * > Instead of sending a single JSON object as the data query parameter,
 * > send a JSON list of objects, base64 encoded, as the data parameter
 * > of an application/x-www-form-urlencoded POST request body.
 *
 */
export function _trackBatch(
  events: Record<string, unknown>[],
  done: () => void,
): void {
  const encoded_data = btoa(JSON.stringify(events))

  _console_log('MIXPANEL BATCH REQUEST:')
  _console_log(events)

  let mixpanelApiHost = 'https://api-js.mixpanel.com'
  try {
    mixpanelApiHost = mixpanel.get_config('api_host')
  } catch (error) {
    Sentry.captureException(error)
  }

  axios
    .post(`${mixpanelApiHost}/track/?ip=1&verbose=1`, `data=${encoded_data}`, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    .then((response) => {
      _console_log('MIXPANEL RESPONSE:')
      _console_log(response.data)
      // Mixpanel always responds with 200 OK, status = 0 in the case of error
      // and status = 1 if everything is actually OK.
      if (response.data.status !== 1) {
        Sentry.captureMessage(
          'Mixpanel batch track error: ' + JSON.stringify(response.data),
        )
      }
    })
    .catch((error) => {
      Sentry.captureException(error)
    })
    .finally(function () {
      done()
    })
}

export function hasMixpanel(): boolean {
  if (typeof mixpanel !== 'undefined' && mixpanel) {
    return true
  }
  return false
}

export function _console_log(...data: any[]): void {
  // We can have 'mixpanel' object, but without the 'get_config' function when
  // mixpanel is not fully loaded yet.
  if (mixpanel && mixpanel.get_config && mixpanel.get_config('debug')) {
    /* eslint-disable no-console */
    console.log(data)
    /* eslint-enable no-console */
  }
}

/**
 * Get Mixpanel super properties (properties that are added to every `mixpanel.track` request).
 */
export async function getMixpanelSuperProperties(): Promise<
  Record<string, any>
> {
  const deviceInfo = await platform.deviceInfo()

  const superProperties: Record<string, any> = {
    Platform: deviceInfo.platform,
    'Native App Version': deviceInfo.appVersion,
    'Device Model': deviceInfo.model,
    'Device OS': deviceInfo.osVersion,
    'User Agent': navigator.userAgent,
    'Web App Title': APP_TITLE,
    'Web App Version': APP_VERSION,
  }

  // `router()` is `undefined` on public pages.
  const currentRoute = router()?.currentRoute?.value
  if (currentRoute?.name) {
    superProperties['Current Route'] = currentRoute?.name.toString()
  } else {
    superProperties['Current Route'] = undefined
  }

  // If linking from a doc content page to a different doc,
  // note the previous page as the "Referrer Doc".
  const previousPage = backLink.value?.previousPage
  if (
    previousPage?.meta?.isDocContentPage &&
    currentRoute?.params.url_slug !== previousPage?.params.url_slug
  ) {
    superProperties['Referrer Doc'] = previousPage.path
  }

  // The external billing page open in the in-app browser on iOS,
  // which means that _technically_ it's running on the web platform,
  // but for analytics purposes, we want to count it as an iOS event.
  if (currentRoute?.name === 'billing_apple_external') {
    superProperties['Platform'] = 'ios'
    superProperties['Native App Version'] = currentRoute.query.appVersion16218
  }

  return superProperties
}

/**
 * Get First Page, Initial Referrer, and Marketing URL data from local storage.
 */
function getFirstPageData(): {
  firstPage: string
  marketingUrl: string
  initialReferrer: string
} {
  return {
    firstPage: storage.getItem(FIRST_PAGE_KEY) || 'No first page',
    marketingUrl: storage.getItem(MARKETING_URL_KEY) || 'No marketing url',
    initialReferrer: storage.getItem(REF_KEY) || 'No referrer',
  }
}

/**
 * Get initial referrer data from local storage.
 */
export function getInitialReferrer(): {
  $initial_referrer: string
  $initial_referring_domain: string
} {
  const initialReferrer = storage.getItem(REF_KEY) || '$direct'
  const initialReferringDomain = storage.getItem(REF_DOMAIN_KEY) || '$direct'

  return {
    $initial_referrer: initialReferrer,
    $initial_referring_domain: initialReferringDomain,
  }
}
